import React, {useState, useMemo} from 'react';
import {
	Modal,
	FlexRow,
	Button,
	DateRangePicker,
	Text,
	TextField,
	Icon,
	FlexColumn,
	DropdownList,
	Frame,
	DraggableContainer,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import Util from '../../forecast-app/shared/util/util';
import {dateObjectToIsoDate, dateFragmentPropsFromIntervalInIsoFormat} from '../../forecast-app/shared/util/DateUtil';
import {DragDropContext, Draggable, Droppable, DropResult} from 'react-beautiful-dnd';
import {DatePickerOnChange} from '../../components/expense-management/PredefinedDateRangePicker';
import {sortByStartDate} from '../../forecast-app/project-tab/projects/project-retainer-periods/RetainerPeriodUtil';
import BulkUpdatePhasesMutation from '../../mutations/bulk_update_phases_mutation';
import DeletePhaseMutation from '../../mutations/delete_phase_mutation';
import {RoleRow, ExpenseRow, PhaseModalPhaseNode, Project} from '../../components/baseline/BaselineTypes';

export type Phase = {
	node: PhaseModalPhaseNode;
};

const ORDER_OPTIONS = {
	CUSTOM: 'custom',
	ASCENDING: 'start_date_ascending',
	DESCENDING: 'start_date_descending',
};

type PhaseManagementModalProps = {
	closeModal: () => void;
	initialPhases: Phase[];
	project: Project;
	baselineItems: (RoleRow | ExpenseRow)[];
};

const PhaseManagementModal = ({
	closeModal,
	initialPhases,
	project,
	baselineItems,
}: PhaseManagementModalProps): React.JSX.Element => {
	const {formatMessage} = useIntl();
	const [changesMade, setChangesMade] = useState(false);
	const projectId = project.id;

	const phasesSortedBySortOrder = initialPhases
		.filter(phase => phase && phase.node && phase.node.sortOrder !== undefined)
		.sort((a, b) => a.node.sortOrder - b.node.sortOrder);
	const [phases, setPhases] = useState<Phase[]>(phasesSortedBySortOrder);
	const [newPhasesCount, setNewPhasesCount] = useState<number>(0);
	const numberOfPhases = phases.length;
	const findOrderSetting = () => {
		const phaseIds = phases.map(phase => phase.node.id);
		const sortedPhaseIds = [...phases].sort((a, b) => sortByStartDate(a.node, b.node)).map(phase => phase.node.id);
		if (phaseIds.every((id, index) => id === sortedPhaseIds[index])) {
			return ORDER_OPTIONS.ASCENDING;
		}
		if (phaseIds.every((id, index) => id === sortedPhaseIds[sortedPhaseIds.length - 1 - index])) {
			return ORDER_OPTIONS.DESCENDING;
		}
		return ORDER_OPTIONS.CUSTOM;
	};
	const [phaseOrder, setPhaseOrder] = useState(findOrderSetting);
	const dragAndDropGroup = 'Phases Group';
	const orderedPhaseIds = useMemo(() => {
		return phases.map(phase => phase.node.id);
	}, [phases]);

	const handlePhaseManagement = () => {
		closeModal();
		if (changesMade) {
			if (phases.length === 0) {
				for (const initialPhase of initialPhases) {
					Util.CommitMutation(DeletePhaseMutation, {
						id: initialPhase.node.id,
						projectId: projectId,
					});
				}
			} else {
				const mutationObject = {
					phases: phases.map((phase, index) => ({
						id: phase.node.id.startsWith('new-phase') ? undefined : phase.node.id,
						projectId: projectId,
						name: phase.node.name,
						startYear: phase.node.startYear,
						startMonth: phase.node.startMonth,
						startDay: phase.node.startDay,
						deadlineYear: phase.node.deadlineYear,
						deadlineMonth: phase.node.deadlineMonth,
						deadlineDay: phase.node.deadlineDay,
						sortOrder: index,
					})),
				};
				Util.CommitMutation(BulkUpdatePhasesMutation, mutationObject);
			}
		}
	};

	const moveToPosition = (sourceIndex: number, destinationIndex: number) => {
		const updatedOrderedPhaseIds = Array.from(orderedPhaseIds);
		const [movedPhaseId] = updatedOrderedPhaseIds.splice(sourceIndex, 1);
		updatedOrderedPhaseIds.splice(destinationIndex, 0, movedPhaseId);
		const updatedPhases = updatedOrderedPhaseIds.map(id => {
			const phase = phases.find(phase => phase.node.id === id);
			if (!phase) {
				throw new Error(`Phase with id ${id} not found`);
			}
			return phase;
		});
		setPhases(updatedPhases);
		setChangesMade(true);
		setPhaseOrder('custom');
	};

	const onPhaseOrderChange = (newOrder: string) => {
		setPhaseOrder(newOrder);
		setChangesMade(true);
		const updatedPhases = [...phases];
		switch (newOrder) {
			case ORDER_OPTIONS.ASCENDING:
				updatedPhases.sort((a, b) => sortByStartDate(a.node, b.node));
				break;
			case ORDER_OPTIONS.DESCENDING:
				updatedPhases.sort((a, b) => sortByStartDate(b.node, a.node));
				break;
			default:
				return;
		}
		setPhases(updatedPhases);
		setChangesMade(true);
	};

	const handleOnDragEnd = (result: DropResult) => {
		if (!result.destination) return;
		const {source, destination} = result;
		moveToPosition(source.index, destination.index);
	};

	const setPhaseName = (name: string, index: number) => {
		const updatedPhases = [...phases];
		if (updatedPhases[index]) {
			updatedPhases[index] = {
				...updatedPhases[index],
				node: {
					...updatedPhases[index].node,
					name: name,
				},
			};
		}
		setPhases(updatedPhases);
		setChangesMade(true);
	};

	const handleDateRangeChange = (date: DatePickerOnChange, index: number) => {
		if (!date.startDate || !date.endDate) {
			return;
		}
		const updatedDateRange = dateFragmentPropsFromIntervalInIsoFormat(date.startDate, date.endDate);
		const updatedPhases = [...phases];
		if (updatedPhases[index]) {
			updatedPhases[index] = {
				...updatedPhases[index],
				node: {
					...updatedPhases[index].node,
					startDay: updatedDateRange.startDay,
					startMonth: updatedDateRange.startMonth,
					startYear: updatedDateRange.startYear,
					deadlineDay: updatedDateRange.endDay,
					deadlineMonth: updatedDateRange.endMonth,
					deadlineYear: updatedDateRange.endYear,
				},
			};
		}
		setPhases(updatedPhases);
		setChangesMade(true);
	};

	const addNewPhase = () => {
		const newIndex = newPhasesCount + 1;
		setNewPhasesCount(newIndex);
		const newPhase = {
			node: {
				id: 'new-phase' + newIndex,
				projectId: projectId,
				startYear: project.projectStartYear,
				startDay: project.projectStartDay,
				startMonth: project.projectStartMonth,
				deadlineYear: project.projectEndYear,
				deadlineDay: project.projectEndDay,
				deadlineMonth: project.projectEndMonth,
				name: 'New Phase ' + newIndex,
				sortOrder: phases.length,
			},
		};
		setPhases(prevPhases => [...prevPhases, newPhase]);
		setChangesMade(true);
	};

	const phasesWithEstimates = useMemo(
		() => new Set(baselineItems ? baselineItems.map(item => item.phaseId) : []),
		[baselineItems]
	);

	const onDeleteSuccess = (index: number) => {
		const updatedPhases = [...phases];
		updatedPhases.splice(index, 1);
		setPhases(updatedPhases);
		setChangesMade(true);
	};

	const onDeletePhase = (index: number, phase: Phase) => {
		showModal({
			type: MODAL_TYPE.DELETE_PHASE_MODAL,
			callback: () => {
				onDeleteSuccess(index);
			},
			hasEstimates: phasesWithEstimates.has(phase.node.id),
		});
	};

	const getContextMenuOptions = (phase: Phase, index: number) => {
		const disableTop = index === 0;
		const disableBottom = index === phases.length - 1;
		const contextMenuOptions = [
			{
				name: 'Move to the top',
				onClick: () => {
					moveToPosition(index, 0);
				},
				disabled: disableTop,
			},
			{
				name: 'Move up',
				onClick: () => {
					moveToPosition(index, index - 1);
				},
				disabled: disableTop,
			},
			{
				name: 'Move down',
				onClick: () => {
					moveToPosition(index, index + 1);
				},
				disabled: disableBottom,
			},
			{
				name: 'Move to the bottom',
				onClick: () => {
					moveToPosition(index, phases.length - 1);
				},
				disabled: disableBottom,
			},
			{
				name: 'Delete Phase',
				onClick: () => {
					onDeletePhase(index, phase);
				},
			},
		];
		return contextMenuOptions;
	};

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal} setHeight>
			<Modal.Title>
				<FormattedMessage id={'phase_management.manage_phases'} />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					<FlexRow gap={'m'}>
						<Button emphasis={'medium'} onPress={addNewPhase} data-cy={'add-new-phase'}>
							<FormattedMessage id={'phase_management.new_phase'} />
						</Button>
						<DropdownList>
							<DropdownList.Trigger>
								<Button emphasis={'medium'} rightIcon={'chevronDown'} disabled={phases.length === 0}>
									<FormattedMessage id={'phase_management.order'} />
									<FormattedMessage id={`phase_management.${phaseOrder}`} />
								</Button>
							</DropdownList.Trigger>
							<DropdownList.Content align={'start'}>
								{Object.values(ORDER_OPTIONS).map(option => (
									<DropdownList.Item onClick={() => onPhaseOrderChange(option)}>
										<FormattedMessage id={'phase_management.order'} />
										<FormattedMessage id={`phase_management.${option}`} />
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						</DropdownList>
					</FlexRow>
					<FlexRow>
						<Icon icon={'phase'} />
						<Text size={'2'}>
							{formatMessage({id: 'phase_management.phases'}, {numberOfPhases: numberOfPhases})}
						</Text>
					</FlexRow>
					{phases.length !== 0 ? (
						<DragDropContext
							scrollYSpeed={25}
							onDragEnd={handleOnDragEnd}
							outerScrollBar={true}
							dragAndDropGroup={dragAndDropGroup}
						>
							<Droppable
								dragAndDropGroup={dragAndDropGroup}
								droppableId="phasesDroppable"
								dynamicElemHeight={true}
								minElemHeight={40}
							>
								{provided => (
									<div {...provided.droppableProps} ref={provided.innerRef}>
										<FlexColumn gap={'s'}>
											{phases.map((phase, index) => (
												<Draggable
													key={phase.node.id}
													draggableId={phase.node.id}
													index={index}
													dragAndDropGroup={dragAndDropGroup}
													noCancelOnMove={true}
												>
													{provided => (
														<div
															ref={provided.innerRef}
															{...provided.draggableProps}
															{...provided.dragHandleProps}
														>
															<DraggableContainer>
																<TextField
																	data-cy={'new-project-name'}
																	value={phase.node.name || ''}
																	onValueChange={value => setPhaseName(value, index)}
																/>
																<DateRangePicker
																	startDate={dateObjectToIsoDate(
																		Util.CreateNonUtcMomentDate(
																			phase.node.startYear,
																			phase.node.startMonth,
																			phase.node.startDay
																		)
																	)}
																	endDate={dateObjectToIsoDate(
																		Util.CreateNonUtcMomentDate(
																			phase.node.deadlineYear,
																			phase.node.deadlineMonth,
																			phase.node.deadlineDay
																		)
																	)}
																	onChange={(date: DatePickerOnChange) =>
																		handleDateRangeChange(date, index)
																	}
																	aria-label="Select date range"
																	data-cy={'date-range-picker'}
																	disabled={false}
																/>
																<DraggableContainer.ContextMenu
																	contextMenuOptions={getContextMenuOptions(phase, index)}
																/>
															</DraggableContainer>
														</div>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</FlexColumn>
									</div>
								)}
							</Droppable>
						</DragDropContext>
					) : (
						<Frame height={'s'} centerContent={true}>
							<FlexRow justifyContent={'center'}>
								<FormattedMessage id={'phase_management.no_phases'} />
							</FlexRow>
						</Frame>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={handlePhaseManagement}>
					<FormattedMessage id="common.save_changes" />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id="common.cancel" />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default PhaseManagementModal;
